import { useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useRunTargetareClasificare } from "@/hooks/api/mutate/useRunTargetareClasificare";
import { useGetTargetareStats } from "@/hooks/api/query/useGetTargetareStats";
import { useGetTargetareUniversComplet } from "@/hooks/api/query/useGetTargetareUniversComplet";
import type { TargetareFiltre } from "@/types/targetare";
import { definitieRaportIncadrare } from "@/utils/targetareRaportIncadrare";
import { FisaFirmaDialog } from "../components/FisaFirmaDialog";
import { celulaFirma } from "../components/raport/CelulaFirma";
import { RaportInteractiv } from "../components/raport/RaportInteractiv";
import { useToast } from "@/hooks/use-toast";
import {
  descriereRezultatClasificare,
  jobActiv,
} from "@/utils/targetareJoburi";
import { StatisticiTargetare } from "../components/StatisticiTargetare";
import { SurseDate } from "../components/SurseDate";

const ANI_DISPONIBILI = [
  { an: 2025, eticheta: "2025 (bilanț 2025, ONRC 02.09.2026)", activ: true },
  {
    an: 2024,
    eticheta: "2024 (bilanțul 2024 nu este încă încărcat)",
    activ: false,
  },
];

const FILTRE_SCOP: TargetareFiltre = { doar_scop_dimensiune: true };

export default function IncadrareTargetare() {
  const [cuiSelectat, setCuiSelectat] = useState<string | null>(null);
  const { toast } = useToast();
  const [an, setAn] = useState(2025);
  const stats = useGetTargetareStats();
  const univers = useGetTargetareUniversComplet(
    FILTRE_SCOP,
    stats.data?.ultima_clasificare
  );
  const clasificare = useRunTargetareClasificare();
  const jobInCurs = jobActiv(stats.data?.joburi ?? []);

  const ruleaza = () => {
    clasificare.mutate(
      { an },
      {
        onSuccess: (raspuns) => {
          toast({
            title:
              raspuns.data.stare === "programat"
                ? "Clasificare programată"
                : "Clasificare",
            description: descriereRezultatClasificare(raspuns.data),
          });
        },
        onError: (eroare) => {
          toast({
            title: "Clasificarea a eșuat",
            description: eroare.message,
            variant: "destructive",
          });
        },
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-xl font-heading font-semibold">
            Încadrarea firmelor
          </h2>
          <p className="max-w-3xl text-sm text-muted-foreground">
            Aplică în masă regulile din formularul de notificare: dimensiunea
            din bilanț (OUG 155/2024, Legea 124/2025), codurile CAEN principal
            și autorizate, listele oficiale și neoficiale, scorul de
            probabilitate per serviciu. Rezultatul: tier (nivelul de încredere
            al potrivirii), încadrarea estimată și sectoarele.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(an)} onValueChange={(v) => setAn(Number(v))}>
            <SelectTrigger className="w-[320px]">
              <SelectValue placeholder="An de referință" />
            </SelectTrigger>
            <SelectContent>
              {ANI_DISPONIBILI.map((a) => (
                <SelectItem key={a.an} value={String(a.an)} disabled={!a.activ}>
                  {a.eticheta}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={clasificare.isPending || !!jobInCurs}>
                {clasificare.isPending || jobInCurs ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Rulează încadrarea
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Reclasificăm toate firmele?</AlertDialogTitle>
                <AlertDialogDescription>
                  Clasificarea existentă este înlocuită cu una nouă, calculată
                  din bilanțurile {an}, codurile CAEN autorizate și listele de
                  autorizați; nivelul de risc estimat se recalculează automat.
                  Durează câteva minute.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Renunță</AlertDialogCancel>
                <AlertDialogAction onClick={ruleaza}>Rulează</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {jobInCurs && (
        <p className="rounded-md border border-ds-blue-200 bg-ds-blue-50 p-3 text-sm text-ds-blue-700">
          Job #{jobInCurs.id} ({jobInCurs.tip}) rulează în fundal. Statisticile
          se actualizează automat la final.
        </p>
      )}

      <StatisticiTargetare stats={stats.data} isLoading={stats.isLoading} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Regulile aplicate</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <span className="font-medium text-foreground">Dimensiune.</span>{" "}
            Salariați, cifră de afaceri și active din situațiile financiare,
            convertite la cursul din setări; regula strictă (salariați sau cifră
            și active) dă dimensiunea folosită la încadrare, iar firmele mici cu
            cel puțin un criteriu la 80% din pragul de mijlocie sunt marcate
            „aproape de prag”.
          </p>
          <p>
            <span className="font-medium text-foreground">Potriviri.</span> CAEN
            principal din bilanț (versiunea Rev. 2/3 rezolvată prin codurile
            ONRC), orice cod CAEN autorizat, prezența pe listele oficiale și
            neoficiale, toate mapate pe cele 77 de servicii din tabela folosită
            și de formularul de notificare.
          </p>
          <p>
            <span className="font-medium text-foreground">
              Nivel de încadrare (tier).
            </span>{" "}
            A: CAEN principal + dimensiune; B: listă + dimensiune; C: serviciu
            în scop indiferent de dimensiune (art. 5–6); D: doar coduri
            secundare, fără încadrare.
          </p>
          <p>
            <span className="font-medium text-foreground">
              Încadrare estimată.
            </span>{" "}
            Regulile clasificatorului v2.3 pe tip de serviciu și dimensiune,
            aplicate potrivirilor pe CAEN principal și liste; condițiile care în
            formular depind de răspunsul utilizatorului iau rezultatul implicit.
            Scorul este maximul din ponderile per serviciu (tip de ambiguitate
            CAEN și listă); pragul de 80% nu este aplicat ca filtru, ci
            disponibil ca filtru „Scor ≥ 80”.
          </p>
        </CardContent>
      </Card>

      {stats.data && (
        <SurseDate
          snapshoturi={stats.data.snapshoturi}
          joburi={stats.data.joburi}
        />
      )}
      <section className="space-y-2">
        <h3 className="text-base font-heading font-semibold">
          Firmele încadrate, cu serviciile și scorurile lor
        </h3>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Fiecare firmă din scop, cu serviciile selectate (scorul scenariului
          peste prag), scorurile tuturor potrivirilor, sectoarele rezultate și
          încadrarea estimată. Click pe rând deschide fișa firmei cu explicația
          fiecărei potriviri.
        </p>
        <RaportInteractiv
          definitie={definitieRaportIncadrare}
          randuri={univers.data ?? []}
          isLoading={univers.isLoading}
          selectat={cuiSelectat}
          onSelect={(cui) => setCuiSelectat(cui)}
          celula={celulaFirma}
          claseRand={(r) => (r.insolventa ? "opacity-70" : undefined)}
        />
      </section>
      <FisaFirmaDialog cui={cuiSelectat} onClose={() => setCuiSelectat(null)} />
    </div>
  );
}
